import { useState, useEffect, useCallback } from 'react';
import sanitizeText from '@/utils/sanitizeText';
import { 
  FaDownload, 
  FaTrash, 
  FaUndo,
  FaSync,
  FaCheckCircle,
  FaExclamationTriangle,
  FaClock,
  FaServer
} from 'react-icons/fa';
import './RouterBackupManagement.scss';
import * as Sentry from '@sentry/react';

/**
 * RouterBackupManagement Component
 * Manages router configuration backups with create, restore, and delete operations
 */
const RouterBackupManagement = ({ routerId, apiFetch }) => {
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [backups, setBackups] = useState([]);
  const [description, setDescription] = useState('');
  const [safeModeTimeout, setSafeModeTimeout] = useState(300);

  /**
   * Load all backups for this router
   */
  const loadBackups = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiFetch(`/api/RouterConfig/${routerId}/backups`);

      if (!response.ok) {
        throw new Error('Failed to load backups');
      }

      const data = await response.json();
      if (data.success) {
        setBackups(data.data || []);
      } else {
        throw new Error(data.error || 'Failed to load backups');
      }
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error loading backups:', err);
      setError(err.message || 'Failed to load backups');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, routerId]);

  useEffect(() => {
    (async () => { await loadBackups(); })();
  }, [loadBackups]);

  /**
   * Create a new manual backup
   */
  const handleCreateBackup = async () => {
    setCreating(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await apiFetch(`/api/RouterConfig/${routerId}/backups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: description || null })
      });

      if (!response.ok) {
        throw new Error('Failed to create backup');
      }

      const data = await response.json();
      if (data.success) {
        setSuccess('Backup created successfully');
        setDescription('');
        await loadBackups();
      } else {
        throw new Error(data.error || 'Failed to create backup');
      }
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error creating backup:', err);
      setError(err.message || 'Failed to create backup');
    } finally {
      setCreating(false);
    }
  };

  /**
   * Restore a backup
   */
  const handleRestoreBackup = async (backupId) => {
    if (!confirm('Are you sure you want to restore this backup? The router will enter safe mode.')) {
      return;
    }

    setRestoring(backupId);
    setError(null);
    setSuccess(null);

    try {
      const response = await apiFetch(`/api/RouterConfig/backups/${backupId}/restore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ safeModeTimeoutSeconds: safeModeTimeout })
      });

      if (!response.ok) {
        throw new Error('Failed to restore backup');
      }

      const data = await response.json();
      if (data.success) {
        setSuccess('Backup restored successfully. Please confirm safe mode on the router.');
        await loadBackups();
      } else {
        throw new Error(data.error || 'Failed to restore backup');
      }
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error restoring backup:', err);
      setError(err.message || 'Failed to restore backup');
    } finally {
      setRestoring(null);
    }
  };

  /**
   * Format file size
   */
  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  /**
   * Format date
   */
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="router-backup-management">
        <div className="loading-message">
          <FaSync className="fa-spin" />
          <span>Loading backups...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="router-backup-management">
      <div className="backup-header">
        <h3>Configuration Backups</h3>
        <button 
          className="btn btn-primary"
          onClick={loadBackups}
          disabled={loading}
        >
          <FaSync /> Refresh
        </button>
      </div>

      {error && (
        <div className="alert alert-danger">
          <FaExclamationTriangle /> {sanitizeText(error)}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <FaCheckCircle /> {sanitizeText(success)}
        </div>
      )}

      {/* Create Backup Section */}
      <div className="create-backup-section">
        <h4>Create Manual Backup</h4>
        <div className="create-backup-form">
          <input
            type="text"
            className="form-control"
            placeholder="Optional description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={creating}
          />
          <button
            className="btn btn-success"
            onClick={handleCreateBackup}
            disabled={creating}
          >
            {creating ? (
              <>
                <FaSync className="fa-spin" /> Creating...
              </>
            ) : (
              <>
                <FaDownload /> Create Backup
              </>
            )}
          </button>
        </div>
        <p className="help-text">
          Creates a full configuration export of the router. Pre-change backups are created automatically.
        </p>
      </div>

      {/* Restore Settings */}
      <div className="restore-settings">
        <label>Safe Mode Timeout (seconds):</label>
        <input
          type="number"
          className="form-control"
          value={safeModeTimeout}
          onChange={(e) => setSafeModeTimeout(parseInt(e.target.value))}
          min="60"
          max="600"
        />
        <p className="help-text">
          Router will automatically rollback if not confirmed within this time.
        </p>
      </div>

      {/* Backups List */}
      <div className="backups-list">
        <h4>Backup History ({backups.length})</h4>
        
        {backups.length === 0 ? (
          <div className="no-backups">
            <FaServer />
            <p>No backups found. Create your first backup above.</p>
          </div>
        ) : (
          <div className="backups-table-container">
            <table className="backups-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Description</th>
                  <th>Size</th>
                  <th>Version</th>
                  <th>Status</th>
                  <th>Restored</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {backups.map((backup) => (
                  <tr key={backup.id} className={!backup.isSuccessful ? 'backup-failed' : ''}>
                    <td>
                      <FaClock /> {formatDate(backup.backupDate)}
                    </td>
                    <td>
                      <span className={`backup-type ${backup.backupType.toLowerCase()}`}>
                        {sanitizeText(backup.backupType)}
                      </span>
                    </td>
                    <td>{sanitizeText(backup.description || '-')}</td>
                    <td>{formatSize(backup.sizeBytes)}</td>
                    <td>{sanitizeText(backup.routerOSVersion || 'Unknown')}</td>
                    <td>
                      {backup.isSuccessful ? (
                        <span className="status-success">
                          <FaCheckCircle /> Success
                        </span>
                      ) : (
                        <span className="status-failed" title={backup.errorMessage || 'Unknown error'}>
                          <FaExclamationTriangle /> Failed
                          {backup.errorMessage && (
                            <small style={{ display: 'block', fontSize: '0.75em', color: '#999', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {sanitizeText(backup.errorMessage)}
                            </small>
                          )}
                        </span>
                      )}
                    </td>
                    <td>
                      {backup.restoreCount > 0 ? (
                        <span className="restore-count">
                          {backup.restoreCount}x
                          {backup.lastRestoredDate && (
                            <small> ({formatDate(backup.lastRestoredDate)})</small>
                          )}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="actions">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handleRestoreBackup(backup.id)}
                        disabled={restoring === backup.id || !backup.isSuccessful}
                        title="Restore this backup"
                      >
                        {restoring === backup.id ? (
                          <FaSync className="fa-spin" />
                        ) : (
                          <FaUndo />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default RouterBackupManagement;
